import { siteDay } from "@/lib/site-time";
import { useTranslation } from "react-i18next";
import { VERSION } from "@/lib/version";
import { LocaleLink, type PublicPath } from "@/components/site/LocaleLink";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { Container } from "@/components/site/Container";
import { BrandWordmark } from "@/components/site/BrandWordmark";
import { addressLine, business } from "@/content/site";
import { CookieSettingsLink } from "@/components/site/cookie-settings-link";

const footerLinks: { diensten: { to: PublicPath; label: string }[]; bedrijf: { to: PublicPath; label: string }[] } = {
  diensten: [
    { to: "/nieuwbouw", label: "Nieuwbouw zwembaden" },
    { to: "/renovatie", label: "Renovatie monoblock" },
    { to: "/technieken", label: "Technieken & lekdetectie" },
    { to: "/warmtepompen-afdekkingen", label: "Warmtepompen & afdekkingen" },
  ],
  bedrijf: [
    { to: "/realisaties", label: "Realisaties" },
    { to: "/blog", label: "Blog" },
    { to: "/over-ons", label: "Over ons" },
    { to: "/offerte", label: "Offerte aanvragen" },
  ],
};

const headingClass =
  "mb-6 font-display text-[13px] font-semibold uppercase tracking-[0.2em] text-secondary";
const linkClass = "text-sm text-primary-foreground/80 transition-colors hover:text-secondary";

export function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="bg-primary text-primary-foreground">
      <Container className="py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-4">
            <LocaleLink to="/" className="inline-block">
              <BrandWordmark />
            </LocaleLink>
            <p className="max-w-[70ch] text-sm leading-relaxed text-primary-foreground/80">
              Specialist in bouwkundige zwembaden, polyester en vinylester bekleding, monoblock renovatie en zwembadtechniek.
              Totaalprojecten van A tot Z, vanuit Maasmechelen actief in Limburg en Zuid-Nederland.
            </p>
            <p className="text-xs text-primary-foreground/60">BTW {business.vat}</p>
          </div>

          <div>
            <h3 className={headingClass}>Diensten</h3>
            <ul className="space-y-3">
              {footerLinks.diensten.map((link) => (
                <li key={link.to}>
                  <LocaleLink to={link.to} className={linkClass}>
                    {link.label}
                  </LocaleLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className={headingClass}>Bedrijf</h3>
            <ul className="space-y-3">
              {footerLinks.bedrijf.map((link) => (
                <li key={link.to}>
                  <LocaleLink to={link.to} className={linkClass}>
                    {link.label}
                  </LocaleLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className={headingClass}>Contact</h3>
            <ul className="space-y-3">
              <li>
                <a href={`tel:${business.phoneE164}`} className={`flex items-center gap-2 ${linkClass}`}>
                  <Phone className="h-4 w-4 text-secondary" strokeWidth={1.75} />
                  {business.phone}
                </a>
              </li>
              <li>
                <a href="https://wa.me/32484976477" className={`flex items-center gap-2 ${linkClass}`}>
                  <MessageCircle className="h-4 w-4 text-secondary" strokeWidth={1.75} />
                  WhatsApp
                </a>
              </li>
              <li>
                <a href={`mailto:${business.email}`} className={`flex items-center gap-2 ${linkClass}`}>
                  <Mail className="h-4 w-4 text-secondary" strokeWidth={1.75} />
                  {business.email}
                </a>
              </li>
              <li>
                <span className="flex items-start gap-2 text-sm text-primary-foreground/80">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-secondary" strokeWidth={1.75} />
                  <span>
                    {addressLine}
                    <br />
                    Werkgebied: {business.areaLabel}
                  </span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-primary-foreground/15 pt-8 md:flex-row md:items-center">
          <p className="text-xs text-primary-foreground/60">
            &copy; {siteDay().slice(0, 4)} Gopro Projects. Alle rechten voorbehouden. <span className="ml-2">{t("footer.version", { version: VERSION })}</span>
          </p>
          <div className="flex gap-6 text-xs text-primary-foreground/60">
            <LocaleLink to="/privacybeleid" className="hover:text-secondary">{t("footer.privacy")}</LocaleLink>
            <LocaleLink to="/cookiebeleid" className="hover:text-secondary">{t("footer.cookies")}</LocaleLink>
            <LocaleLink to="/sitemap" className="hover:text-secondary">Sitemap</LocaleLink>
            <CookieSettingsLink className="hover:text-secondary" />
          </div>
        </div>
      </Container>
    </footer>
  );
}
